import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import { useTheme } from '../../DataStore/ThemeContext';
import { useReport } from '../../DataStore/ReportContext';
import functions from '../../util/functions';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import printContent from '../../util/araknerdlib';
import AlertDateSelector from '../../components/Report/AlertDateSelector';
import LetterHead from '../../components/Securities/LetterHead';
import Signatory from '../../components/Securities/Signatory';
import { useCurrency } from '../../util/SystemVariables';

const TrialBalancePage = () => {

    const {currentNotify} = useTheme();
    const {getTrialBalanceReport, trialBalanceReport, dispatch, TBDetailLevel} = useReport();
    const statementRef = useRef(); // Ref to enclose statement content for printing
    const [showAlert, setShowAlert] = useState(false);
    const currency = useCurrency();

    // Fetch trial balance data whenever the level changes
    useEffect(() => {
        getTrialBalanceReport()
    }, []);

    const handleReset = ()=>{
        dispatch({ type: 'TBDetailLevel', payload: 0 });
    }

    const reduceDetail = ()=>{
        dispatch({ type: 'TBDetailLevel', payload:  TBDetailLevel + 1 });
    }

    const handleConfirmDateRange = async (range) => {
        dispatch({ type: 'dateRange', payload: range });
        await getTrialBalanceReport(true, range);
      };

    return (
        <>
        <PageContainer>
            <MainContent>
                {/* Page Title */}
                <PageTitle
                    title="Trial Balance"
                    description="List of balances for the general ledger accounts during this financial period."
                    iconClass="fas fa-chart-line"
                    sticky={true}
                    PageActions={() => (
                        <>
                            <ActionDropdownItem iconClass="fa fa-calendar-alt text-success" label="Filter Date Range" onClick={()=>setShowAlert(true)}/>
                            <ActionDropdownItem iconClass="fas fa-search-plus text-danger" label="Reduce Detail Level ..." onClick={reduceDetail}/>
                            <ActionDropdownItem iconClass="fa fas fa-binoculars text-info" label="Reset Detail Level" onClick={handleReset}/>
                            <ActionDropdownItem iconClass="fa fa-print" label="Print Statement" onClick={()=>printContent(statementRef, "Trial Balance")}/>
                            <ActionDropdownItem iconClass="fa fa-sync text-success" label="Refresh" onClick={()=>getTrialBalanceReport(true)} />
                        </>
                    )}
                />
                {currentNotify}
                
                 {/* Statement Content */}
                 <div ref={statementRef}>
                    <LetterHead />
                    <div className="content-wrapper d-flex">
                        {/* Statement Header */}
                        <div className="header-section" style={{ flex: 2 }}>
                            <h4>Trial Balance</h4>
                            <p>Balances of all ledger accounts<br/>
                            <strong>Currency:</strong> {currency || 'N/A'}
                            </p>

                            {functions.isObject(trialBalanceReport) && trialBalanceReport.summary && ( <p>
                                <strong>Detail Level: </strong> {trialBalanceReport.summary.level} <br/>
                                <strong>Financial Period: </strong>{' '}
                                {trialBalanceReport.summary.opening_date && <>{functions.formatDate(trialBalanceReport.summary.opening_date)} - {functions.formatDate(trialBalanceReport.summary.closing_date)}</>}
                            </p>)}
                        </div>

                        {/* Statement Summary */}
                        {functions.isObject(trialBalanceReport) && trialBalanceReport.summary && (
                            <div className="summary-table" style={{ flex: 1 }}>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Total Debits</th>
                                            <td>{functions.numberFormat(trialBalanceReport.totals.total_debit)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Credits</th>
                                            <td>{functions.numberFormat(trialBalanceReport.totals.total_credit)}</td>
                                        </tr>
                                        <tr>
                                            <th>Comment</th>
                                            <td>{trialBalanceReport.summary.balanced ? "Trial Balance is valid" : "Trial Balance is invalid"}</td>

                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="card mb-4">
                        <div className="card-body">
                        <div className="table-responsive">
                        {functions.isObject(trialBalanceReport) && <><table className="table table-bordered">
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                    <tr>
                                        <th>Account Number /Category</th>
                                        <th>Account Name</th>
                                        <th className="text-right">Debit (Dr)</th>
                                        <th className="text-right">Credit (Cr)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(trialBalanceReport.trial_balance).map(([category, accounts]) => (
                                        <React.Fragment key={category}>
                                            {/* Subheading Row for Category */}
                                            <tr>
                                                <td colSpan="4" className="font-weight-bold bg-light">
                                                    {category}
                                                </td>
                                            </tr>
                                            {/* Rows for Accounts */}
                                            {accounts.map((account, index) => (
                                                <tr key={index}>
                                                    <td style={{ paddingLeft: '20px' }}>{account.account_number}</td>
                                                    <td>{account.account}</td>
                                                    <td className="text-right">
                                                        {account.total_debit > 0 ? functions.numberFormat(account.total_debit) : ''}
                                                    </td>
                                                    <td className="text-right">
                                                        { account.total_credit > 0 ? functions.numberFormat(account.total_credit) : ''}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                <tfoot>
                                    {/* Totals Row */}
                                    <tr>
                                        <th>&nbsp;</th>
                                        <th style={{textAlign:'right'}}>Total</th>
                                        <th className="text-right">{functions.numberFormat(trialBalanceReport.totals.total_debit)}</th>
                                        <th className="text-right">{functions.numberFormat(trialBalanceReport.totals.total_credit)}</th>
                                    </tr>
                                </tfoot>
                            </table>
                            <Signatory />
                            </>}
                            {!trialBalanceReport && <ActivityLoader />}
                            {trialBalanceReport === "404" && <Notification message="Trial balance could not be loaded at this time"/>}
                            </div>
                        </div>
                    </div>


                        
                       


                    </div>

            </MainContent>
        </PageContainer>
        <AlertDateSelector
            show={showAlert}
            onClose={() => setShowAlert(false)}
            onConfirm={handleConfirmDateRange}
        />
        </>
    );
};

export default TrialBalancePage;
